/**
 * Κοινά κομμάτια των σελίδων του οργανισμού στη νέα εμφάνιση (14/10/2026): υπο-καρτέλες σελίδας,
 * μήνυμα σε γραμμή (ok/προσοχή/σφάλμα/πληροφορία) και το μικρό μήνυμα επάνω δεξιά.
 *
 * Γιατί δικά τους και όχι του shared/listPage: το listPage δεν έχει υπο-καρτέλες (οι σελίδες του
 * καταστήματος τις έχουν στο πλαϊνό μενού), ενώ ο κεντρικός κατάλογος, ο κατάλογος πλατφορμών και
 * τα πρότυπα έχουν δεύτερο επίπεδο μέσα στη σελίδα· και το Notice του listPage ξέρει μόνο «προσοχή»
 * και «σφάλμα», ενώ εδώ υπάρχουν και μηνύματα επιτυχίας που μένουν (όχι toast).
 * Μόνο tokens (bg-surface-*, text-t*, border-line, brand): ανοιχτό «Χαρτί» και σκούρο «Γραφίτης».
 */
import React, { useRef } from 'react';
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';

/* ───────────────────────────── υπο-καρτέλες ───────────────────────────── */

export interface SubTab<K extends string> {
  id: K;
  label: React.ReactNode;
  /** Πλήθος δίπλα στο όνομα (π.χ. «Προϊόντα 246»)· null = κανένα. */
  count?: number | null;
  /** Μικρό σήμα που ζητά προσοχή (π.χ. διαφορές τιμών). */
  badge?: React.ReactNode;
  testId?: string;
}

export interface SubTabsProps<K extends string> {
  tabs: Array<SubTab<K>>;
  value: K;
  onChange: (id: K) => void;
  /** Όνομα της ομάδας για τους αναγνώστες οθόνης. */
  label: string;
  testId?: string;
}

/**
 * Δεύτερο επίπεδο πλοήγησης μέσα σε σελίδα (ίδιο σχήμα με το «Προϊόντα · Ομάδες επιλογών …» του
 * Καταλόγου): ένα κουτί με κουμπιά, το επιλεγμένο στο απαλό χρώμα της εφαρμογής. role=tablist —
 * βελάκια αριστερά/δεξιά περνούν στην επόμενη καρτέλα, όπως σε κάθε tablist.
 */
export function SubTabs<K extends string>({ tabs, value, onChange, label, testId }: SubTabsProps<K>): React.ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft' && event.key !== 'Home' && event.key !== 'End') return;
    event.preventDefault();
    const last = tabs.length - 1;
    let next = index;
    if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
    else if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
    else if (event.key === 'Home') next = 0;
    else next = last;
    onChange(tabs[next].id);
    const buttons = ref.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    buttons?.[next]?.focus();
  };
  return (
    <div
      ref={ref}
      role="tablist"
      aria-label={label}
      data-testid={testId}
      className="inline-flex max-w-full gap-0.5 overflow-x-auto rounded-xl border border-line bg-surface-2 p-[3px] [scrollbar-width:none]"
    >
      {tabs.map((tab, index) => {
        const active = tab.id === value;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            data-testid={tab.testId}
            onClick={() => onChange(tab.id)}
            onKeyDown={e => onKeyDown(e, index)}
            className={`inline-flex h-9 shrink-0 items-center gap-2 whitespace-nowrap rounded-[9px] px-3.5 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand ${
              active ? 'bg-brand-soft font-semibold text-brand' : 'font-medium text-t2 hover:bg-surface-3/70 hover:text-t1'
            }`}
          >
            {tab.label}
            {tab.count !== undefined && tab.count !== null && (
              <span className={`text-xs font-medium tabular-nums ${active ? 'opacity-75' : 'text-t4'}`}>{tab.count}</span>
            )}
            {tab.badge}
          </button>
        );
      })}
    </div>
  );
}

/* ───────────────────────────── μήνυμα σε γραμμή ───────────────────────────── */

export type BannerTone = 'ok' | 'warn' | 'danger' | 'info';

const BANNER_TONE: Record<BannerTone, string> = {
  ok: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-900 dark:text-emerald-200',
  warn: 'border-amber-500/35 bg-amber-500/10 text-amber-900 dark:text-amber-200',
  danger: 'border-red-500/30 bg-red-500/10 text-red-800 dark:text-red-300',
  info: 'border-line bg-surface-2 text-t2',
};

/**
 * Μήνυμα που ΜΕΝΕΙ στη σελίδα (όχι toast): αποτέλεσμα εισαγωγής, 409 «τρέχει ήδη», κατάσταση
 * «ανάγνωσης». Ένα εικονίδιο 16px που λέει το είδος, κείμενο που διαβάζεται και στο σκούρο, και
 * προαιρετικά «×» για να κλείσει.
 */
export const Banner: React.FC<{
  tone: BannerTone;
  children: React.ReactNode;
  onDismiss?: () => void;
  dismissLabel?: string;
  /** Ένα κουμπί/σύνδεσμος δεξιά (π.χ. «Έγκριση όλων»). */
  action?: React.ReactNode;
  icon?: React.ElementType | null;
  role?: 'status' | 'alert' | 'note';
  testId?: string;
  className?: string;
}> = ({ tone, children, onDismiss, dismissLabel, action, icon, role, testId, className = '' }) => {
  const Icon = icon === null ? null : (icon || (tone === 'ok' ? CheckCircle2 : tone === 'info' ? Info : AlertTriangle));
  return (
    <div
      role={role}
      data-testid={testId}
      className={`flex items-start gap-2.5 rounded-xl border px-3.5 py-2.5 text-sm ${BANNER_TONE[tone]} ${className}`.trim()}
    >
      {Icon && <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />}
      {action ? (
        // Με ενέργεια: όταν δεν χωρά δίπλα στο κείμενο (κινητό, στενό συρτάρι) πέφτει από κάτω του,
        // αντί να στριμώχνει το κείμενο σε μια στήλη λίγων λέξεων.
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-2.5">
          <div className="min-w-0 flex-1 basis-64">{children}</div>
          <div className="shrink-0">{action}</div>
        </div>
      ) : (
        <div className="min-w-0 flex-1">{children}</div>
      )}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={dismissLabel}
          title={dismissLabel}
          className="-mr-1 shrink-0 rounded-lg p-1 opacity-70 transition hover:bg-black/5 hover:opacity-100 dark:hover:bg-white/10"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

/* ───────────────────────────── μικρό μήνυμα επάνω δεξιά ───────────────────────────── */

export interface FlashMessage {
  type: 'success' | 'error';
  message: string;
}

/**
 * Το «Αποθηκεύτηκε» / «Αποτυχία …» των σελίδων που το κρατούν οι ίδιες (λίγα δευτερόλεπτα, επάνω
 * δεξιά) — ίδια όψη με το shared/Toast της εφαρμογής: επιφάνεια, λεπτό χρωματιστό περίγραμμα,
 * εικονίδιο. Πάνω από το συρτάρι (z 80 > 70), ώστε να φαίνεται και όσο αυτό είναι ανοιχτό.
 */
export const FlashToast: React.FC<{ toast: FlashMessage | null; testId?: string }> = ({ toast, testId = 'org-toast' }) => {
  if (!toast) return null;
  const ok = toast.type === 'success';
  const Icon = ok ? CheckCircle2 : AlertTriangle;
  return (
    <div
      role={ok ? 'status' : 'alert'}
      data-testid={testId}
      className={`fixed right-4 top-4 z-[80] flex max-w-sm items-start gap-2.5 rounded-xl border bg-surface-1 px-4 py-3 text-sm text-t1 shadow-lg ${
        ok ? 'border-emerald-500/40' : 'border-red-500/40'
      }`}
    >
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`} aria-hidden="true" />
      <span className="min-w-0">{toast.message}</span>
    </div>
  );
};

/* ───────────────────────────── μικρά ───────────────────────────── */

/** Κείμενο-κουμπί μέσα σε πρόταση ή κάτω από πεδίο (π.χ. «+ Μέγεθος»), στο χρώμα της εφαρμογής. */
export const LINK_BTN = 'inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline underline-offset-2 '
  + 'disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline';

/** Μικρό κουμπί εικονιδίου μέσα σε γραμμή πίνακα (✎, 🗑) — 36px στόχος, χωρίς περίγραμμα. */
export const ROW_ICON_BTN = 'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-t3 transition-colors '
  + 'hover:bg-surface-3 hover:text-t1 disabled:opacity-40 disabled:cursor-not-allowed';

/** Ίδιο, για την επικίνδυνη ενέργεια της γραμμής (Διαγραφή). */
export const ROW_DANGER_BTN = 'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-t3 transition-colors '
  + 'hover:bg-red-500/10 hover:text-red-700 dark:hover:text-red-400 disabled:opacity-40 disabled:cursor-not-allowed';

/** Πεδίο μέσα σε πίνακα/λίστα επεξεργασίας (μεγέθη, επιλογές): ίδιο με το INPUT_CLS σε χαμηλότερο ύψος. */
export const COMPACT_INPUT = 'min-w-0 h-9 px-2.5 border border-line-strong rounded-lg bg-surface-2 text-t1 placeholder-t4 text-sm '
  + 'focus:outline-none focus:ring-2 focus:ring-ring/50 focus:border-ring disabled:opacity-50';

/** Τσιπ επιλογής (κατάστημα, ημέρα): πατημένο = απαλό χρώμα της εφαρμογής. */
export const chipClass = (on: boolean): string => `inline-flex items-center gap-1.5 min-h-[36px] px-3 rounded-lg border text-sm transition-colors ${
  on ? 'border-brand-line bg-brand-soft font-semibold text-brand' : 'border-line-strong bg-surface-2 text-t2 hover:bg-surface-3'
}`;

/** Επικεφαλίδα ομάδας μέσα σε συρτάρι ή σελίδα (μικρό κεφαλαίο, όχι κουτί). */
export const GroupTitle: React.FC<{ children: React.ReactNode; aside?: React.ReactNode; className?: string }> = ({ children, aside, className = '' }) => (
  <div className={`flex items-center justify-between gap-3 ${className}`.trim()}>
    <h3 className="text-[11.5px] font-bold uppercase tracking-[0.06em] text-t3">{children}</h3>
    {aside}
  </div>
);
